
import { PostgrestError } from '@supabase/supabase-js';
import { 
  checkCombinationExists, 
  checkNameUniqueness,
  updateExistingState,
  insertNewState
} from './saveStateUtils';
import { SaveStateParams } from '../useSaveStateMutations';
import { getErrorMessage } from './errorHandlers';

/**
 * Updates an existing state with new data
 */
export const handleStateUpdate = async (
  id: number, 
  params: SaveStateParams
): Promise<{ result: any, error?: Error }> => {
  console.log(`Updating existing state with ID: ${id}`);
  
  try {
    const { data: updateResult, error: updateError } = await updateExistingState(id, params);

    if (updateError) {
      console.error('Error updating state:', updateError);
      throw new Error(getErrorMessage(updateError));
    }
    
    console.log('Update result:', updateResult);
    return { result: updateResult };
  } catch (error) {
    console.error('Error in handleStateUpdate:', error);
    return { result: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
};

/**
 * Creates a new state or updates an existing one if constraints are violated
 */
export const handleStateCreate = async (
  params: SaveStateParams
): Promise<{ result: any, error?: Error }> => {
  const { name, country, league, year, data } = params;
  console.log('Creating new state');
  
  try {
    // First check if there's a unique constraint violation based on combination
    const { data: existingData, error: checkError } = await checkCombinationExists(country, league, year);
        
    if (checkError) {
      console.error('Error checking existing state:', checkError);
      throw new Error(`Error checking for existing state: ${checkError.message}`);
    }
        
    if (existingData) {
      // If it exists, update it
      console.log('Combination exists, updating instead. ID:', existingData.id);
      return await handleStateUpdate(existingData.id, { name, country, league, year, data });
    }
    
    // Check if there's a name conflict before inserting
    const { exists: nameExists, error: nameCheckError } = await checkNameUniqueness(name);
        
    if (nameCheckError) {
      console.error('Error checking name uniqueness:', nameCheckError);
      throw new Error(`Error checking for name uniqueness: ${nameCheckError.message}`);
    }
        
    if (nameExists) {
      throw new Error("A state with this name already exists. Please use a different name.");
    }
    
    // Proceed with insert if no conflicts
    const { data: insertResult, error: insertError } = await insertNewState(params);

    if (insertError) {
      console.error('Error saving state:', insertError);
      
      // Handle specific insert errors
      if (insertError.code === '23505') {
        // This means a unique constraint was violated
        const constraintName = insertError.details?.includes('name') 
          ? 'name'
          : 'country/league/year combination';
            
        console.log(`Constraint violation on ${constraintName}, attempting update`);
        
        if (constraintName === 'name') {
          throw new Error("A state with this name already exists. Please use a different name.");
        }
        
        // Handle country/league/year combination constraint
        const { data: existingState, error: fetchError } = await checkCombinationExists(country, league, year);
            
        if (fetchError) {
          console.error('Error fetching existing state:', fetchError);
          throw new Error(`Error identifying existing record: ${fetchError.message}`);
        }
        
        if (!existingState) {
          throw new Error("Could not resolve the constraint violation.");
        }
        
        // Then update it
        return await handleStateUpdate(existingState.id, params);
      } 
      
      throw new Error(getErrorMessage(insertError));
    }
    
    console.log('Insert result:', insertResult);
    return { result: insertResult };
  } catch (error) {
    console.error('Error in handleStateCreate:', error);
    return { result: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
};

/**
 * Main function to save state data
 */
export const saveState = async (params: SaveStateParams): Promise<any> => {
  const { id, isUpdate } = params;
  console.log('Starting saveStateMutation with data:', { 
    name: params.name, 
    country: params.country, 
    league: params.league, 
    year: params.year, 
    dataLength: params.data.length,
    isUpdate,
    id
  });
  
  try {
    if (isUpdate && id) {
      const { result, error } = await handleStateUpdate(id, params);
      if (error) throw error;
      return result;
    } else {
      const { result, error } = await handleStateCreate(params);
      if (error) throw error;
      return result;
    }
  } catch (error) {
    console.error('Error in saveState:', error);
    throw error;
  }
};

